import { Message } from './../../../models/message.model';
import { SkeletonModule } from 'primeng/skeleton';
import { EmailSequencesResponse, EmailTemplatesResponse, ProspectListsResponse, IntegrationsResponse, PlatformEmailAccountsResponse, CreateEmailSequenceResponse, CreateEmailSequenceInPlatformsResponse } from './../../../models/api-response.model';
import { EmailSequence, NewSequence, NewSequenceResponse, SequenceStatus, SequenceStep, TemplateModel, TimeUnit } from './../../../models/email-sequence.model';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OutreachService } from '../../../services/outreach.service';
import { Contact } from '../../../models/message.model';
import { DataView } from 'primeng/dataview';

import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { ToolbarModule } from 'primeng/toolbar';
import { TagModule } from 'primeng/tag';
import { TabMenuModule } from 'primeng/tabmenu';
import { PanelModule } from 'primeng/panel';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { TabViewModule } from 'primeng/tabview';
import { InputNumberModule } from 'primeng/inputnumber';

import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { MessageModule } from 'primeng/message';
import { Editor, EditorModule } from 'primeng/editor';
import { TooltipModule } from 'primeng/tooltip';
import { ProspectImportDialogComponent } from 'src/app/components/prospect-import-dialog/prospect-import-dialog.component';
import { ProspectService } from 'src/app/services/prospect.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, forkJoin, of, Subject, takeUntil, tap } from 'rxjs';
import { IntegrationsService } from 'src/app/services/integrations.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { Integration } from 'src/app/models/integration.model';
import { SequenceMetricsDetailsModalComponent } from 'src/app/components/sequence-metrics-details-modal/sequence-metrics-details-modal.component';
import { SelectableCompany } from 'src/app/models/company.model';
import { CompaniesService } from 'src/app/services/companies.service';
import { SelectModule } from 'primeng/select';
import { CapitalizeWordsPipe } from 'src/app/pipes/capitalize-words.pipe';
import { ProspectSelectModalComponent } from 'src/app/components/prospect-select-modal/prospect-select-modal.component';
import { ProgressSpinner } from 'primeng/progressspinner';
import { MultiSelectModule } from 'primeng/multiselect';
import { NewEmailSequenceComponent } from 'src/app/components/email-outreach/new-email-sequence/new-email-sequence.component';
import { NewEmailTemplateComponent } from 'src/app/components/email-outreach/new-email-template/new-email-template.component';
import { EmailSequenceTableComponent } from 'src/app/components/email-outreach/email-sequence-table/email-sequence-table.component';
import { CompanyHeaderComponent } from 'src/app/components/company-header/company-header.component';
import { RolePermissionsService } from 'src/app/services/role-permissions.service';

@Component({
  selector: 'app-email-outreach-main',
  templateUrl: './email-outreach-main.component.html',
  styleUrls: ['./email-outreach-main.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DropdownModule,
    CardModule,
    EditorModule,
    ConfirmDialogModule,
    ProgressSpinner,
    ToolbarModule,
    SelectModule,
    TagModule,
    TabMenuModule,
    PanelModule,
    InputTextModule,
    CheckboxModule,
    TabViewModule,
    InputNumberModule,
    ToastModule,
    ProspectImportDialogComponent,
    TooltipModule,
    DataView,
    SkeletonModule,
    MultiSelectModule,
    SequenceMetricsDetailsModalComponent,
    // CapitalizeWordsPipe, // Not used in template
    ProspectSelectModalComponent,
    NewEmailSequenceComponent,
    NewEmailTemplateComponent,
    EmailSequenceTableComponent,
    CompanyHeaderComponent
  ],
  providers: [MessageService, ConfirmationService]
})
export class EmailOutreachMainComponent implements OnInit, OnDestroy {
  // Company selection properties
  currentCompany: SelectableCompany | null = null;
  isLoading = false;
  dataLoaded = false;
  private currentLoadingCompanyId: string | null = null;
  private tabsDataLoaded = {
    sequences: false,
    templates: false,
    prospects: false
  };
  
  // Pagination support
  sequencesPagination = { page: 1, limit: 50, hasMore: true };
  templatesPagination = { page: 1, limit: 50, hasMore: true };
  
  // Main navigation and view state
  activeView: 'sequences' | 'templates' | 'prospects' = 'sequences';
  @ViewChild(Editor) editor?: Editor;
  
  // Sequence related properties
  sequences: EmailSequence[] = [];
  selectedSequences: EmailSequence[] = [];
  creatingNewSequence: boolean = false;
  editingSequence: boolean = false;
  currentEditingSequence: EmailSequence | null = null;
  
  // Template related properties
  templates: TemplateModel[] = [];
  selectedTemplates: TemplateModel[] = [];
  editingTemplate: boolean = false;
  creatingNewTemplate: boolean = false;
  prospects: Contact[] = [];
  availableProspects: Contact[] = [];
  previewBody = '';
  
  // Loading states
  loading: boolean = true;
  templatesLoading: boolean = true;
  prospectsLoading: boolean = true;
  sequencePreparationLoading: boolean = false;
  prospectLayout: 'grid' | 'list' = 'grid';
  prospectViewOptions = ['list', 'grid'];
  
  // Navigation elements
  tabs: MenuItem[];
  activeTab: MenuItem;
  showImportDialog = false;
  private destroy$ = new Subject<void>();
  integrations: Integration[] = [];
  selectedIntegration: Integration | null = null;
  
  // Define available platforms (including HubSpot) - these are available for sequence creation
  private availablePlatforms: Integration[] = [
    {
      id: 'instantly',
      platform: 'instantly',
      name: 'Instantly.ai',
      logo: '/assets/images/instantly-logo.png',
      description: 'Cold email outreach and automation platform.',
      connected: false,
      enabled: false,
      url: 'https://instantly.ai',
      type: 'email',
      supported: true
    },
    {
      id: 'smartlead',
      platform: 'smartlead',
      name: 'SmartLead',
      logo: 'assets/images/smartlead.webp',
      description: 'Advanced cold email automation with unlimited mailboxes and AI-powered deliverability.',
      connected: false,
      url: 'https://smartlead.ai',
      enabled: false,
      type: 'email',
      supported: true
    },
    {
      id: 'hubspot',
      platform: 'hubspot',
      name: 'HubSpot',
      logo: '/assets/images/hubspot.jpeg',
      description: 'Increase Leads — Generate leads, close deals, and create remarkable customer experiences.',
      connected: false,
      enabled: false,
      url: 'https://www.hubspot.com',
      type: 'email',
      supported: true
    },
    {
      id: 'apollo',
      platform: 'apollo',
      name: 'Apollo',
      logo: '/assets/images/apollo.png',
      description: 'Use Apollo to find your ideal prospects & turn your process into a revenue machine.',
      connected: false,
      enabled: false,
      url: 'https://www.apollo.io',
      type: 'email',
      supported: true
    }
  ];
  selectedMetricsSequence: EmailSequence | null = null;
  showMetricsModal = false;
  loadingMessage = '';
  hasTriedToSave = false;
  isSaving = false;
  
  // Dropdown options
  statuses: SequenceStatus[] = [
    { label: 'Active', value: 'active' },
    { label: 'Paused', value: 'paused' },
    { label: 'Draft', value: 'draft' },
    { label: 'Completed', value: 'completed' }
  ];
  prospectLists = [];
  emailTemplates: { label: string; value: string }[] = [];

  timeUnits: TimeUnit[] = [
    { label: 'Days', value: 'days' },
  ];

  // Filter states
  selectedStatus: SequenceStatus | null = null;

  // Form models
  newSequence: NewSequence = {
    sequenceId: '',
    name: '',
    emailAccounts: [],
    description: '',
    prospectListId: '',
    steps: [
      {
        type: 'initial',
        templateId: null,
        abTestingEnabled: false
      }
    ],
    integration: ''
  };

  currentTemplate: TemplateModel = {
    name: '',
    subject: '',
    body: '',
    type: 'email'
  };

  // Preview data
  previewData: any = {
    company: 'Acme Corp',
    first_name: 'John',
    last_name: 'Doe',
    title: 'Marketing Director',
    company_size: '500-1000',
    industry: 'Technology'
  };
  isProspectSelectModalVisible = false;
  selectedProspectList: any;
  selectedSequence: any;
  platformEmailAccounts: any[] = [];
  selectedPlatformEmailAccounts: any[] = [];
  
  // Permission properties
  canCreateSequences = true;
  canImportProspects = true;
  canCreateTemplates = true;
  canEditSequences = true;
  canDeleteSequences = true;
  canManageSequences = true;
  canEditTemplates = true;
  canDeleteTemplates = true;
  
  constructor(
    private emailOutreachService: OutreachService,
    private router: Router,
    private authService: AuthService,
    private integrationsService: IntegrationsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private prospectService: ProspectService,
    private companiesService: CompaniesService,
    private rolePermissionsService: RolePermissionsService) {
    this.tabs = [
      { label: 'Sequences', icon: 'pi pi-list' },
      { label: 'Templates', icon: 'pi pi-file' },
      { label: 'Prospects', icon: 'pi pi-users' }
    ];

    this.activeTab = this.tabs[0];
  }

  ngOnInit(): void {
    this.setupCompanySubscription();
    this.initializeLoadingState();
  }

  private setupCompanySubscription(): void {
    // Subscribe to company changes with debouncing to prevent rapid successive calls
    this.companiesService.selectedCompany$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev?.companyId === curr?.companyId),
        debounceTime(300), // Wait 300ms before processing company changes
        tap(company => console.log('Company emission (debounced):', company))
      )
      .subscribe(company => {
        if (company && company.companyId) {
          // Only load if this is a different company
          if (company.companyId !== this.currentCompany?.companyId) {
            console.log('Company changed from', this.currentCompany?.companyId, 'to', company.companyId);
            this.currentCompany = company;
            this.updatePermissions();
            this.loadDataWithOptimizations();            
          }
        } else {
          console.log('No valid company received:', company);
          this.currentCompany = null;
          // Clear data when no company is selected
          this.sequences = [];
          this.templates = [];
          this.prospects = [];
          this.integrations = [];
        }
      });
  }


  isViewingParent(): boolean {
    return this.companiesService.isCurrentSelectionParent();
  }

  canManageUsers(): boolean {
    return this.companiesService.hasPermission('manage_users');
  }

  canWrite(): boolean {
    return this.companiesService.hasPermission('write');
  }

  private updatePermissions(): void {
    // For email outreach, users need 'write' permission for any modifications
    const canWrite = this.rolePermissionsService.canCurrentUserWrite();
    const canManageSeq = this.rolePermissionsService.currentUserHasPermission('manage_sequences');
    const canDelete = this.rolePermissionsService.currentUserHasPermission('delete');
    
    // Creation permissions
    this.canCreateSequences = canWrite && canManageSeq;
    this.canCreateTemplates = canWrite && canManageSeq;
    this.canImportProspects = canWrite;
    
    // Modification permissions
    this.canEditSequences = canWrite;
    this.canDeleteSequences = canWrite && (canDelete || canManageSeq);
    this.canManageSequences = canWrite && canManageSeq;
    this.canEditTemplates = canWrite;
    this.canDeleteTemplates = canWrite && (canDelete || canManageSeq);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeLoadingState(): void {
    this.loading = true;
    this.templatesLoading = true;
    this.prospectsLoading = true;

    this.sequences = [];
    this.templates = [];
    this.emailTemplates = [];
    this.prospects = [];
  }

  // Optimized data loading - only load sequences (first tab) immediately
  loadDataWithOptimizations() {
    // Don't load data if no company is selected
    if (!this.currentCompany?.companyId) {
      console.log('No company selected, skipping data load');
      return;
    }

    const companyId = this.currentCompany.companyId;

    // Prevent duplicate calls for the same company
    if (this.currentLoadingCompanyId === companyId) {
      console.log('Already loading data for this company:', companyId);
      return;
    }

    // Prevent any loading if already in progress
    if (this.isLoading) {
      console.log('Already loading, skipping duplicate call');
      return;
    }

    this.currentLoadingCompanyId = companyId;
    this.isLoading = true;
    this.dataLoaded = false;
    
    // Reset tab loading states
    this.tabsDataLoaded = {
      sequences: false,
      templates: false,
      prospects: false
    };

    console.log(`Loading Email data with optimizations for company: ${companyId} (${this.currentCompany.companyName})`);

    // Only load sequences (first tab) immediately - other tabs will load on demand
    this.loadSequencesData(companyId, 1, true);
    
    // Load integrations (always needed for webhooks)
    this.loadIntegrationsData(companyId);
  }

  private loadAllData(): void {
    forkJoin({
      sequences: this.emailOutreachService.getEmailSequences(this.currentCompany!.companyId),
      templates: this.emailOutreachService.getEmailTemplates(this.currentCompany!.companyId),
      prospects: this.prospectService.getProspectLists(this.currentCompany!.companyId, 'email'),
      integrations: this.integrationsService.getIntegrations(this.authService.userId(), this.currentCompany!.companyId)
    }).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load data'
        });
        return of({ 
          sequences: { data: { sequences: [] } } as EmailSequencesResponse, 
          templates: { data: { templates: [] } } as EmailTemplatesResponse, 
          prospects: { data: { lists: [] } } as ProspectListsResponse, 
          integrations: { data: [] } as IntegrationsResponse 
        });
      }),
      finalize(() => {
        this.loading = false;
        this.templatesLoading = false;
        this.prospectsLoading = false;
        this.dataLoaded = true;
      })
    ).subscribe({
      next: (result) => {
        this.sequences = result.sequences.data.sequences;
        this.processTemplates(result.templates.data.templates);
        // Merge available platforms with connected integrations
        this.integrations = this.mergeAvailablePlatformsWithConnected(result.integrations.data || []);
        if (this.integrations?.length > 0) {
          this.selectedIntegration = this.integrations[0];
        }
        
        if (result.prospects?.data?.lists) {
          this.prospects = result.prospects.data.lists;
          this.availableProspects = result.prospects.data.lists.filter((e: any) => e.providerListId == null);
        }
      }
    });
  }

  private loadSequencesData(companyId: string, page = 1, forceRefresh = false) {
    if (!forceRefresh && this.tabsDataLoaded.sequences && this.currentCompany?.companyId === companyId && page === 1) {
      console.log('Sequences already loaded for this company');
      return;
    }

    this.loading = true;
    
    this.emailOutreachService.getEmailSequences(companyId)
      .then(result => {
        if (this.currentCompany?.companyId === companyId) {
          const sequences = (result as EmailSequencesResponse)?.data?.sequences || [];
          
          if (page === 1) {
            this.sequences = sequences;
          } else {
            this.sequences = [...this.sequences, ...sequences];
          }
          
          // Update pagination state
          this.sequencesPagination.page = page;
          this.sequencesPagination.hasMore = sequences.length === this.sequencesPagination.limit;
          
          this.tabsDataLoaded.sequences = true;
          console.log(`✅ Email sequences loaded (page ${page})`);
        }
      })
      .catch(error => {
        console.error('Error loading email sequences:', error);
        if (page === 1) {
          this.sequences = [];
        }
      })
      .finally(() => {
        this.isLoading = false;
        this.loading = false;
        this.checkAllDataLoaded();
      });
  }

  private async loadTemplatesData(companyId: string, page = 1, forceRefresh = false): Promise<void> {
    if (!forceRefresh && this.tabsDataLoaded.templates && this.currentCompany?.companyId === companyId && page === 1) {
      console.log('Templates already loaded for this company');
      return;
    }

    this.templatesLoading = true;
    
    try {
      const result = await this.emailOutreachService.getEmailTemplates(companyId);
      
      if (this.currentCompany?.companyId === companyId) {
        const templates = (result as EmailTemplatesResponse)?.data?.templates || [];
        
        if (page === 1) {
          this.processTemplates(templates);
        } else {
          // Append new templates
          const newProcessedTemplates = templates.map((template: any) => ({
            label: template.name,
            value: template.id || ''
          }));
          this.emailTemplates = [...this.emailTemplates, ...newProcessedTemplates];
          
          const newTemplates = templates.map((template: any) => ({
            ...template,
            id: String(template.id), // Ensure ID is always a string
            type: 'email',
            editTemplate: false
          }));
          this.templates = [...this.templates, ...newTemplates];
        }
        
        // Update pagination state
        this.templatesPagination.page = page;
        this.templatesPagination.hasMore = templates.length === this.templatesPagination.limit;
        
        this.tabsDataLoaded.templates = true;
        console.log('Templates:', this.templates);
        console.log(`✅ Email templates loaded (page ${page})`);
      }
    } catch (error) {
      console.error('Error loading email templates:', error);
      if (page === 1) {
        this.templates = [];
        this.emailTemplates = [];
      }
    } finally {
      this.templatesLoading = false;
      this.checkAllDataLoaded();
    }
  }

  private async loadProspectsData(companyId: string, forceRefresh = false): Promise<void> {
    if (!forceRefresh && this.tabsDataLoaded.prospects && this.currentCompany?.companyId === companyId) {
      console.log('Prospects already loaded for this company');
      return;
    }

    this.prospectsLoading = true;

    try {
      const result = await this.prospectService.getProspectLists(companyId, 'email');
      if (this.currentCompany?.companyId === companyId) {
        if (result?.data?.lists) {
          this.prospects = result.data.lists;
          this.availableProspects = result.data.lists.filter((e: any) => e.providerListId == null);
        } else {
          console.warn('No prospects data or unexpected structure:', result);
          this.prospects = [];
        }
        this.tabsDataLoaded.prospects = true;
        console.log('✅ Email prospects loaded');
      }
    } catch (error) {
      console.error('Error loading email prospects:', error);
      this.prospects = [];
    } finally {
      this.prospectsLoading = false;
      this.checkAllDataLoaded();
    }
  }

  private loadIntegrationsData(companyId: string) {
    this.integrationsService.getIntegrations(this.authService.userId(), companyId)
      .then(result => {
        if (this.currentCompany?.companyId === companyId) {
          // Merge available platforms with connected integrations
          this.integrations = this.mergeAvailablePlatformsWithConnected((result as IntegrationsResponse)?.data || []);
          const ints = this.integrations.filter(e => e.type == 'email');
          if (ints?.length > 0) {            
            this.selectedIntegration = ints[0];
          }
          console.log('✅ Email integrations loaded');
        }
      })
      .catch(error => {
        console.error('Error loading email integrations:', error);
        // Even if API fails, show available platforms for sequence creation
        this.integrations = this.mergeAvailablePlatformsWithConnected([]);
      })
      .finally(() => {
        this.checkAllDataLoaded();
      });
  }

  private checkAllDataLoaded() {
    // Only check for sequences (first tab) and integrations for initial load completion
    const sequencesLoaded = this.tabsDataLoaded.sequences;
    const integrationsLoaded = this.integrations.length > 0 || this.currentLoadingCompanyId === null;
    
    if (sequencesLoaded && integrationsLoaded) {
      this.isLoading = false;
      this.dataLoaded = true;
      this.currentLoadingCompanyId = null;
      console.log('🚀 Essential Email data loaded successfully (sequences + integrations)');
    }
  }

  private processTemplates(data: any[]): void {
    if (!data || !data.length) return;

    this.emailTemplates = data.map((template: any) => ({
      label: template.name,
      value: template.id || 0
    }));

    this.templates = data.map((temp: any) => ({
      id: temp.id,
      name: temp.name,
      subject: temp.subject,
      body: temp.body,
      bodyHtml: temp.bodyHtml,
      type: temp.type || 'email',
      createdDate: temp.createdDate,
      createdAt: temp.updatedAt,
      updatedAt: temp.updatedAt
    }));
  }

  // Add method to manually refresh data
  refreshData(): void {
    if (this.currentCompany) {
      console.log('🔄 Manual refresh triggered');
      
      // Reset tab loading states to force reload
      this.tabsDataLoaded = {
        sequences: false,
        templates: false,
        prospects: false
      };
      
      // Reset pagination states
      this.sequencesPagination = { page: 1, limit: 50, hasMore: true };
      this.templatesPagination = { page: 1, limit: 50, hasMore: true };
      
      // Force refresh the current tab data
      const currentTab = this.getCurrentTabName();
      this.lazyLoadTabData(currentTab as 'sequences' | 'templates' | 'prospects', true);
      
      // Also refresh integrations
      this.loadIntegrationsData(this.currentCompany.companyId);
         
    }
  }

  private getCurrentTabName(): string {
    switch (this.activeView) {
      case 'sequences': return 'sequences';
      case 'templates': return 'templates';
      case 'prospects': return 'prospects';
      default: return 'sequences';
    }
  }

  private lazyLoadTabData(tabName: 'sequences' | 'templates' | 'prospects', forceRefresh = false) {
    if (!this.currentCompany?.companyId) return;
    
    const companyId = this.currentCompany.companyId;
    
    // Load data for this tab if not already loaded
    switch (tabName) {
      case 'sequences':
        if (!this.tabsDataLoaded.sequences || forceRefresh) {
          this.loadSequencesData(companyId, 1, forceRefresh);
        }
        break;
      case 'templates':
        if (!this.tabsDataLoaded.templates || forceRefresh) {
          this.loadTemplatesData(companyId, 1, forceRefresh);
        }
        break;
      case 'prospects':
        if (!this.tabsDataLoaded.prospects || forceRefresh) {
          this.loadProspectsData(companyId, forceRefresh);
        }
        break;
    }
  }

  async startSequence(sequence: EmailSequence) {
    try {
      await this.emailOutreachService.startSequence(sequence.providerCampaignId, sequence.provider, sequence.sequenceId, this.currentCompany!.companyId);
      this.refreshData();
      this.messageService.add({
        severity: 'success',
        summary: 'Sequence Started',
        detail: `${sequence.name} has been started on ${sequence.provider}`
      });
      await this.loadSequences();
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to start sequence'
      });
    }
  }

  async pauseSequence(sequence: EmailSequence) {
    try {
      await this.emailOutreachService.pauseSequence(sequence.providerCampaignId, sequence.provider, sequence.sequenceId, this.currentCompany!.companyId);
      this.refreshData();
      this.messageService.add({
        severity: 'success',
        summary: 'Sequence Paused',
        detail: `${sequence.name} has been paused on ${sequence.provider}`
      });
      await this.loadSequences();
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to pause sequence'
      });
    }
  }

  async editSequence(sequence: EmailSequence) {
    try {
      // Show loading indicator
      this.sequencePreparationLoading = true;
      this.loadingMessage = 'Loading sequence for editing...';

      // Ensure prospects are loaded before entering edit mode
      if (!this.tabsDataLoaded.prospects || this.prospectsLoading) {
        this.loadingMessage = 'Loading prospect lists...';
        await this.loadProspectsData(this.currentCompany!.companyId, true);
      }

      // Load sequence steps
      this.loadingMessage = 'Loading sequence steps...';
      const steps = await this.emailOutreachService.getEmailSequenceSteps(sequence.sequenceId, this.currentCompany!.companyId);

      // Store the sequence being edited
      this.currentEditingSequence = sequence;
      
      this.newSequence = { 
        sequenceId: sequence.sequenceId, 
        name: sequence.name, 
        emailAccounts: sequence.emailAccounts, 
        description: sequence.description, 
        steps: steps, 
        prospectListId: sequence.prospectListId, 
        integration: sequence.provider 
      };

      this.editingTemplate = false;
      this.creatingNewTemplate = false;
      this.creatingNewSequence = false;
      this.editingSequence = true;
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error Loading Sequence',
        detail: `An error occurred loading the sequence for editing`
      });
    } finally {
      this.sequencePreparationLoading = false;
      this.loadingMessage = '';
    }
  }

  async loadSequences(): Promise<void> {
    try {
      this.loading = true;
      const data = await this.emailOutreachService.getEmailSequences(this.currentCompany!.companyId);
      const newSequences = data.data.sequences;
      this.sequences = [...newSequences];
      this.loading = false;
    } catch (e) {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load Sequences'
      });
    }
  }

  getSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warn';
      case 'draft':
        return 'info';
      case 'completed':
        return 'secondary';
      default:
        return 'info';
    }
  }

  onTabChange(event: any): void {
    if (event.label === 'Sequences') {
      this.activeView = 'sequences';
      this.creatingNewSequence = false;
      this.creatingNewTemplate = false;
      this.lazyLoadTabData('sequences');
    } else if (event.label === 'Templates') {
      this.activeView = 'templates';
      this.creatingNewSequence = false;
      this.creatingNewTemplate = false;
      this.lazyLoadTabData('templates');
    } else if (event.label === 'Prospects') {
      this.activeView = 'prospects';
      this.creatingNewSequence = false;
      this.creatingNewTemplate = false;
      this.lazyLoadTabData('prospects');
    }
  }

  // Navigation methods
  navigateToSequenceDetails(sequence: EmailSequence): void {
    this.router.navigate(['/email-outreach/sequence', sequence.sequenceId, this.currentCompany!.companyId], { queryParamsHandling: 'merge' });
  }

  navigateToSequenceMetrics(sequence: EmailSequence): void {
    // TODO: Create email outreach metrics route similar to LinkedIn
    // For now, show a helpful message instead of navigating to non-existent route
    this.messageService.add({
      severity: 'info',
      summary: 'Feature Coming Soon', 
      detail: `Metrics for "${sequence.name}" will be available in a future update.`
    });
  }

  // Helper methods for company display
  getCurrentCompanyDisplayName(): string {
    if (this.currentCompany) {
      return this.companiesService.getCompanyDisplayName(this.currentCompany);
    }
    return 'No company selected';
  }

  getLoadingMessage(): string {
    if (this.currentCompany) {
      return `Loading email data for ${this.currentCompany.companyName}...`;
    }
    return 'Loading email data...';
  }

  // Sequence management methods
  async showNewSequenceForm(event: Event): Promise<void> {
    // Set loading state if we need to load data
    const needsTemplates = !this.tabsDataLoaded.templates && this.currentCompany?.companyId;
    const needsProspects = !this.tabsDataLoaded.prospects && this.currentCompany?.companyId;
    
    if (needsTemplates || needsProspects) {
      this.sequencePreparationLoading = true;
      if (needsTemplates && needsProspects) {
        this.loadingMessage = 'Loading templates and prospects...';
      } else if (needsTemplates) {
        this.loadingMessage = 'Loading email templates...';
      } else if (needsProspects) {
        this.loadingMessage = 'Loading prospects...';
      }
    }

    try {
      // Ensure templates are loaded before checking if they exist
      if (needsTemplates) {
        await this.loadTemplatesData(this.currentCompany!.companyId, 1, true);
      }

      // Ensure prospects are loaded for sequence creation
      if (needsProspects) {
        await this.loadProspectsData(this.currentCompany!.companyId, true);
      }
    } finally {
      this.sequencePreparationLoading = false;
      this.loadingMessage = '';
    }

    if (this.integrations?.length == 0) {
      this.confirmationService.confirm({
        target: event.target as EventTarget,
        message: 'You have not set up your integration platforms',
        header: 'Platform Setup',
        closable: true,
        closeOnEscape: true,
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: {
          label: 'Cancel',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Setup Platform',
        },
        accept: () => {
          this.router.navigate(['/integrations'], { queryParamsHandling: 'merge' });
        },
        reject: () => {

        },
      });
    } else if (this.templates?.length == 0) {
      this.confirmationService.confirm({
        target: event.target as EventTarget,
        message: 'You have not defined any campaign templates. Please define your templates before configuring a campaign',
        header: 'Template Setup',
        closable: true,
        closeOnEscape: true,
        icon: 'pi pi-exclamation-triangle',
        rejectButtonProps: {
          label: 'Cancel',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Create Templates',
        },
        accept: () => {
          this.editingTemplate = true;
          this.creatingNewSequence = false;
          this.editingSequence = false;
          this.creatingNewTemplate = false;
        },
        reject: () => {

        },
      });
    }
    this.getEmailAccounts();
    // Reset the form data
    this.newSequence = {
      name: '',
      description: '',
      emailAccounts: [],
      steps: [
        {
          type: 'initial',
          templateId: null,
          abTestingEnabled: false,
          waitDuration: 1,
          waitUnit: 'days'
        },
      ],
      prospectListId: '',
      integration: ''
    };

    this.creatingNewSequence = true;
    this.editingSequence = false;
    this.editingTemplate = false;
    this.creatingNewTemplate = false;
  }

  cancelSequenceCreation(): void {
    this.creatingNewSequence = false;
    this.editingSequence = false;
    this.currentEditingSequence = null;
  }

  async saveSequence(): Promise<void> {
    try {
      this.hasTriedToSave = true;

      // Validate required fields
      if (this.isEmailAccountsInvalid) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select at least one email account'
        });
        return;
      }

      if (!this.newSequence.name || this.newSequence.name.trim().length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter a sequence name'
        });
        return;
      }

      this.newSequence.integration = this.selectedIntegration?.platform || '';
      this.newSequence.emailAccounts = this.selectedPlatformEmailAccounts || [];

      this.newSequence.steps.forEach(step => {
        const temp = this.templates.find(temp => temp.id === step.templateId);
        step.templateName = temp?.name;
        step.templateSubject = temp?.subject;
        step.templateBody = temp?.body;
      });
      
      this.hasTriedToSave = false;
      let platRep: CreateEmailSequenceInPlatformsResponse;
      let createRep: CreateEmailSequenceResponse;
      this.isSaving = true;
      
      if (this.editingSequence) {
        this.loadingMessage = 'Updating sequence...';
        await this.emailOutreachService.updateEmailSequence(this.newSequence, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Sequence updated successfully'
        });
        await this.sleep(1000);
        this.loadingMessage = 'Syncing with integration platforms...';
        await this.emailOutreachService.updateEmailSequenceInPlatforms(this.newSequence.sequenceId || '', this.currentCompany!.companyId);
        this.editingSequence = false;
        this.creatingNewSequence = false;
        await this.loadSequences();
      } else {
        this.loadingMessage = 'Creating sequence...';
        createRep = await this.emailOutreachService.createEmailSequence(this.newSequence, this.selectedIntegration?.platform, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Sequence Saved',
          detail: 'Now adding your sequence to your integration partners'
        });
        await this.sleep(1000);
        this.loadingMessage = `Creating sequence in ${this.selectedIntegration?.platform}`;
        platRep = await this.emailOutreachService.createEmailSequenceInPlatforms(createRep!.data.sequenceId, this.currentCompany!.companyId);
      }
      
      this.editingSequence = false;
      this.creatingNewSequence = false;

      if (this.newSequence.prospectListId && (this.newSequence.prospectListId as any)?.prospectListId?.length > 0) {
        this.loadingMessage = 'Adding leads to your campaign';
        await this.emailOutreachService.associateCampaignWithLeadList(platRep!.data.campaignId, createRep!.data.sequenceId, (this.newSequence.prospectListId as any)?.prospectListId, this.currentCompany!.companyId);
        await this.loadSequences();
        this.messageService.add({
          severity: 'success',
          summary: `Prospect List, ${(this.newSequence.prospectListId as any)?.name} moved`,
          detail: `Emails in the prospect list, ${(this.newSequence.prospectListId as any)?.name}, have been moved to the ${this.newSequence.name} campaign.`
        });
      } else {
        await this.loadSequences();
      }
    } catch (error: any) {
      if (error.error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error.error
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save sequence and add it to your integration partner'
        });
      }
      console.error('Error saving sequence:', error);
    } finally {
      this.isSaving = false;
      this.loadingMessage = '';
    }
  }

  get isEmailAccountsInvalid(): boolean {
    return !this.selectedPlatformEmailAccounts || this.selectedPlatformEmailAccounts.length === 0;
  }

  onEmailAccountsChange(): void {
    if (this.selectedPlatformEmailAccounts && this.selectedPlatformEmailAccounts.length > 0) {
      this.hasTriedToSave = false;
    }
  }

  async deleteSequence(event: Event, sequence: any) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Are you sure you want to delete the sequence "${sequence.name || 'this sequence'}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        try {
          // Show loading state
          this.sequencePreparationLoading = true;
          this.loadingMessage = 'Deleting sequence...';

          const deleteResponse = await this.emailOutreachService.deleteEmailSequence(sequence, this.currentCompany!.companyId);
          
          // Enhanced success message based on platform deletion status
          let successDetail = `${sequence.name} has been deleted`;
          if (deleteResponse?.data?.platformDeleted) {
            successDetail += ' and removed from the email platform';
          } else if (sequence.provider) {
            successDetail += ` (Note: Platform removal may need to be done manually for ${sequence.provider})`;
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Sequence Deleted',
            detail: successDetail
          });
          
          this.refreshData();
        } catch (e: any) {
          console.error('Delete error:', e);
          
          let errorDetail = `${sequence.name} could not be deleted`;
          if (e.error?.message) {
            errorDetail += `: ${e.error.message}`;
          }
          
          this.messageService.add({
            severity: 'error',
            summary: 'Failure to Delete Sequence',
            detail: errorDetail
          });
        } finally {
          this.sequencePreparationLoading = false;
          this.loadingMessage = '';
        }
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Cancelled',
          detail: 'Delete operation cancelled'
        });
      }
    });
  }

  // Template management methods
  showNewTemplateForm(): void {
    this.currentTemplate = {
      name: '',
      subject: '',
      body: '',
      type: 'email'
    };

    this.creatingNewTemplate = true;
    this.editingTemplate = false;
    this.creatingNewSequence = false;
  }

  editTemplate(template: TemplateModel): void {
    this.currentTemplate = { ...template };
    this.editingTemplate = true;
    this.creatingNewTemplate = false;
    this.creatingNewSequence = false;
  }

  cancelTemplateEditing(): void {
    this.editingTemplate = false;
    this.creatingNewTemplate = false;
  }

  async saveTemplate(): Promise<void> {
    try {
      let result;
      const quill = this.editor?.getQuill();
      this.currentTemplate.body = quill.getText();
      this.currentTemplate.type = 'email';
      if (this.currentTemplate.id) {
        result = await this.emailOutreachService.updateEmailTemplate(this.currentTemplate, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template updated successfully'
        });
      } else {
        result = await this.emailOutreachService.createEmailTemplate(this.currentTemplate, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template created successfully'
        });
      }

      this.editingTemplate = false;
      this.creatingNewTemplate = false;
      this.refreshData();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save template'
      });
      console.error('Error saving template:', error);
    }
  }

  async deleteTemplate(template: TemplateModel): Promise<void> {
    if (confirm(`Are you sure you want to delete the template "${template.name}"?`)) {
      try {
        await this.emailOutreachService.deleteEmailTemplate(template.id!, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template deleted successfully'
        });
        this.loadTemplates();
      } catch (e) {
        console.log(e);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete template'
        });
      }
    }
  }

  insertPersonalizationVariable(variable: string): void {
    const quill = this.editor?.getQuill();
    const selection = quill.getSelection() || { index: quill.getLength(), length: 0 };
    quill.insertText(selection.index, `{{${variable}}}`);
    this.previewTemplate();
  }

  previewTemplate(): void {
    const quill = this.editor?.getQuill();
    let previewContent = quill.getText();

    Object.keys(this.previewData).forEach(key => {
      const variablePattern = new RegExp(`{{${key}}}`, 'g');
      previewContent = previewContent.replace(variablePattern, this.previewData[key] || '');
      this.previewBody = previewContent;
    });
  }

  async loadTemplates(): Promise<void> {
    try {
      this.templatesLoading = true;
      const data = await this.emailOutreachService.getEmailTemplates(this.currentCompany!.companyId)
      this.emailTemplates = data.data.templates.map((template: any) => ({
        label: template['name'],
        value: template.id || ''
      }));
      this.templates = data.data.templates.map((temp: any) => {
        return {
          id: temp.id,
          name: temp.name,
          subject: temp.subject,
          body: temp.body,
          type: temp.type || 'email',
          createdDate: temp.createdDate,
          createdAt: temp.updatedAt,
          updatedAt: temp.updatedAt
        }
      })
      this.templatesLoading = false;
    } catch (e) {
      console.log(e);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load Templates'
      });
    }
  }

  // Helper methods for sequence creation
  getFollowUpSteps(): SequenceStep[] {
    return this.newSequence.steps.slice(1);
  }

  addSequenceStep(): void {
    const followUpNumber = this.newSequence.steps.length;
    this.newSequence.steps.push({
      type: `followup${followUpNumber}`,
      templateId: null,
      waitDuration: 1,
      waitUnit: 'days',
      abTestingEnabled: false
    });
  }

  removeStep(index: number): void {
    if (this.newSequence.steps.length > 1) {
      this.newSequence.steps.splice(index, 1);
      for (let i = 1; i < this.newSequence.steps.length; i++) {
        this.newSequence.steps[i].type = `followup${i}`;
      }
    }
  }

  getProgressColor(percentage: number): string {
    if (percentage < 30) return '#FF5252';
    if (percentage < 70) return '#FFA726';
    return '#66BB6A';
  }

  sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  showImportDialogFunc() {
    if (this.integrations.length == 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'You have not set up your integration platforms. Please go to the Integrations Page'
      });
      return;
    }
    this.showImportDialog = true
  }

  async onProspectsImported(event: { file: File, mappings: { [key: string]: string }, listName: string }): Promise<void> {
    try {
      await this.processProspectImport(event.file, event.mappings, event.listName);
      // Force refresh prospects data regardless of current active tab
      if (this.currentCompany?.companyId) {
        this.tabsDataLoaded.prospects = false;
        this.loadProspectsData(this.currentCompany.companyId, true);
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Import Started',
        detail: `Importing ${event.file.name} with ${Object.keys(event.mappings).length} mapped fields`
      });
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Import Failure',
        detail: `An error occurred importing prospects`
      });
    }
  }

  onImportCancelled(): void {

  }

  private async processProspectImport(file: File, mappings: { [key: string]: string }, listName: string): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    formData.append('listName', listName);

    try {
      const rep = await this.prospectService.importProspects(formData, 'email', this.currentCompany!.companyId);
      this.messageService.add({
        severity: 'success',
        summary: 'Import Success',
        detail: `All prospects have been imported. Sending prospects to integration point now.`
      });
    } catch (error) {
      console.error('Error processing import:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Import Failure',
        detail: `An error occurred importing prospects`
      });
    }
  }

  sendEmail(contact: Contact) {
    this.messageService.add({
      severity: 'info',
      summary: 'Email Action',
      detail: `Composing email to ${contact.name} at ${contact.email}`
    });
  }

  markAsContacted(contact: Contact) {
    contact.status = 'Contacted';
    contact.lastContacted = new Date().toISOString().split('T')[0];
    this.messageService.add({
      severity: 'success',
      summary: 'Status Updated',
      detail: `${contact.name} marked as contacted`
    });
  }

  viewDetails(contact: Contact) {
    this.messageService.add({
      severity: 'info',
      summary: 'Prospect Details',
      detail: `Viewing details for ${contact.name}`
    });
  }

  deleteContact(contact: Contact) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${contact.name}?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.prospects = this.prospects.filter(c => c.email !== contact.email);
        this.messageService.add({
          severity: 'success',
          summary: 'Contact Deleted',
          detail: `${contact.name} has been removed from prospects`
        });
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active':
        return 'status-active';
      case 'Contacted':
        return 'status-contacted';
      case 'Responded':
        return 'status-responded';
      default:
        return '';
    }
  }

  openSequenceModal(sequence: EmailSequence): void {
    this.selectedMetricsSequence = sequence;
    this.showMetricsModal = true;
  }

  onModalClose(visible: boolean): void {
    if (!visible) {
      this.showMetricsModal = false;
      this.selectedMetricsSequence = null;
    }
  }

  getEmailIntegrations() {
    const emailIntegrations = this.integrations?.filter(e => e.type == 'email');
    console.log('📧 Email integrations for dropdown:', emailIntegrations?.map(e => ({ id: e.id, name: e.name, type: e.type })));
    return emailIntegrations;
  }

  /**
   * Merge available platforms with connected integrations
   * This ensures all supported platforms are available for sequence creation,
   * whether they're connected or not
   */
  private mergeAvailablePlatformsWithConnected(connectedIntegrations: any[]): Integration[] {
    console.log('🔄 Merging platforms - Available:', this.availablePlatforms.length, 'Connected:', connectedIntegrations.length);
    const merged: Integration[] = [];
    
    // Start with all available platforms
    for (const platform of this.availablePlatforms) {
      // Find if this platform has a connected integration
      const connected = connectedIntegrations.find(
        ci => ci.platform === platform.id || ci.platform === platform.platform
      );
      
      
      if (connected) {
        // Use connected integration data but preserve platform info
        const mergedItem = {
          ...platform,
          connected: true,
          enabled: connected.enabled,
          // Add any other connected integration specific fields
          ...connected,
          // Ensure the type from platform is preserved
          type: platform.type
        };
        merged.push(mergedItem);
      } else {
        // Platform not connected - add as available but not connected
        const mergedItem = {
          ...platform,
          connected: false,
          enabled: false
        };
        merged.push(mergedItem);
      }
    }
    
    // Add any connected integrations that aren't in our available platforms
    for (const connected of connectedIntegrations) {
      const platformExists = merged.some(
        m => m.platform === connected.platform || m.id === connected.platform
      );
      
      if (!platformExists) {
        merged.push({
          id: connected.platform,
          platform: connected.platform,
          name: connected.platform,
          type: connected.type || 'email',
          connected: true,
          enabled: connected.enabled,
          supported: true,
          ...connected
        });
      }
    }
    
    console.log('✅ Merged integrations result:', merged.map(m => ({ id: m.id, name: m.name, type: m.type, connected: m.connected })));
    return merged;
  }

  showProspectSelectModal(sequence: any) {
    this.selectedSequence = sequence;
    this.isProspectSelectModalVisible = true;
  }

  async onProspectListSelected(e: any) {
    try {
      this.selectedProspectList = e.selectedList;
      console.log('Selected prospect list:', e.selectedList);
      const sequence = e.sequence
      await this.emailOutreachService.associateCampaignWithLeadList(sequence.providerCampaignId, sequence.sequenceId, this.selectedProspectList.prospectListId, this.currentCompany!.companyId);
      this.refreshData();
      this.selectedSequence = undefined;
      this.selectedProspectList = null;
      this.messageService.add({
        severity: 'success',
        summary: `Prospect List, ${(this.newSequence.prospectListId as any)?.name} moved`,
        detail: `Emails in the prospect list, ${(e.sequence.prospectListId as any)?.name}, have been moved to the ${e.sequence.name} campaign.`
      });
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `An error occurred moving the prospect list`
      });
    }
  }

  onProspectSelectModalCancelled() {
    this.selectedProspectList = null;
    console.log('Modal was cancelled');
    this.selectedSequence = undefined;
  }

  async getEmailAccounts() {
    try {
      this.selectedPlatformEmailAccounts = [];
      if (this.selectedIntegration?.platform) {
        const result = await this.emailOutreachService.getPlatformEmailAccounts(this.currentCompany!.companyId, this.selectedIntegration.platform);
        this.platformEmailAccounts = result.data;
      }
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to fetch the email accounts for sending'
      });
    }
  }

  // Component integration methods
  onTemplateSaved(): void {
    this.editingTemplate = false;
    this.creatingNewTemplate = false;
    // Force refresh templates data regardless of current active tab
    if (this.currentCompany?.companyId) {
      this.tabsDataLoaded.templates = false;
      this.loadTemplatesData(this.currentCompany.companyId, 1, true);
    }
  }

  onTemplateCancelled(): void {
    this.editingTemplate = false;
    this.creatingNewTemplate = false;
  }

  onSequenceSaved(): void {
    this.editingSequence = false;
    this.creatingNewSequence = false;
    this.currentEditingSequence = null;
    this.refreshData();
  }

  onSequenceCancelled(): void {
    this.editingSequence = false;
    this.creatingNewSequence = false;
    this.currentEditingSequence = null;
  }

  onIntegrationChanged(integration: Integration): void {
    this.selectedIntegration = integration;
    this.getEmailAccounts();
  }

  onProspectsLoadedFromChild(prospects: Contact[]): void {
    console.log('Received prospects from child component:', prospects);
    this.prospects = prospects;
    this.tabsDataLoaded.prospects = true;
    this.prospectsLoading = false;
  }

  getSequenceForEditing(): EmailSequence | null {
    return this.currentEditingSequence;
  }
}